import { useEffect, useMemo, useState } from 'react'
import type { Media, NavItemSetting, SiteSettings } from '@nhanho/shared'
import { renderTitleTemplate } from '@nhanho/shared'
import { settings as api, explainError, type SettingsBody } from '../lib/api'
import { useToast } from '../lib/toast'
import { useDirtyGuard } from '../lib/dirty'
import { PageHeader } from '../components/ui/PageHeader'
import { Card } from '../components/ui/Card'
import { Field } from '../components/ui/Field'
import { Input, Textarea } from '../components/ui/Input'
import { Toggle } from '../components/ui/Toggle'
import { Button } from '../components/ui/Button'
import { Spinner } from '../components/ui/Spinner'
import { TagInput } from '../components/ui/TagInput'
import { ConfirmDialog } from '../components/ui/Modal'
import { ImageField } from '../components/media/ImageField'
import {
  EMPTY_SEO, JsonLdField, PixelMeter, SerpPreview, SocialPreview,
  seoFromMeta, seoToMeta, type SeoValue,
} from '../components/seo/SeoPanel'
import { SEO_LIMITS, textPixels } from '@nhanho/shared'

interface Form {
  siteName: string
  titleTemplate: string
  titleSeparator: string
  defaultMetaDescription: string
  defaultOgImage: Media | null
  allowIndexing: boolean
  defaultBrand: string
  siteUrl: string
  googleSiteVerification: string
  gaMeasurementId: string
  org: { legalName: string; phone: string; email: string; address: string; sameAs: string[] }
  mainNav: NavItemSetting[]
  /** SEO riêng của trang chủ — dùng lại đúng khối `meta` như sản phẩm/bài viết. */
  home: SeoValue
  robotsDisallow: string[]
  robotsExtra: string
  sitemapExclude: string[]
}

const fromDoc = (s: SiteSettings): Form => ({
  siteName: s.siteName,
  titleTemplate: s.titleTemplate,
  titleSeparator: s.titleSeparator,
  defaultMetaDescription: s.defaultMetaDescription ?? '',
  defaultOgImage: typeof s.defaultOgImage === 'object' ? (s.defaultOgImage as Media | null) : null,
  allowIndexing: s.allowIndexing,
  defaultBrand: s.defaultBrand ?? '',
  siteUrl: s.siteUrl ?? '',
  googleSiteVerification: s.googleSiteVerification ?? '',
  gaMeasurementId: s.gaMeasurementId ?? '',
  org: {
    legalName: s.organization?.legalName ?? '',
    phone: s.organization?.phone ?? '',
    email: s.organization?.email ?? '',
    address: s.organization?.address ?? '',
    sameAs: s.organization?.sameAs ?? [],
  },
  mainNav: (s.mainNav ?? []).map((n) => ({ label: n.label, href: n.href, order: n.order ?? 0 })),
  home: s.home ? seoFromMeta(s.home) : EMPTY_SEO,
  robotsDisallow: s.robotsDisallow ?? [],
  robotsExtra: s.robotsExtra ?? '',
  sitemapExclude: s.sitemapExclude ?? [],
})

const toBody = (f: Form): SettingsBody => ({
  siteName: f.siteName,
  titleTemplate: f.titleTemplate,
  titleSeparator: f.titleSeparator,
  defaultMetaDescription: f.defaultMetaDescription,
  defaultOgImage: f.defaultOgImage?.id ?? null,
  allowIndexing: f.allowIndexing,
  defaultBrand: f.defaultBrand,
  siteUrl: f.siteUrl,
  googleSiteVerification: f.googleSiteVerification,
  gaMeasurementId: f.gaMeasurementId,
  organization: f.org,
  // Bỏ dòng trống, và đánh lại `order` theo đúng thứ tự đang thấy trên màn hình.
  mainNav: f.mainNav
    .filter((n) => n.label.trim() && n.href.trim())
    .map((n, i) => ({ label: n.label.trim(), href: n.href.trim(), order: i })),
  home: seoToMeta(f.home),
  // Bỏ dòng trống. API đòi đường dẫn bắt đầu bằng `/`, nên tự thêm thay vì trả 422 —
  // gõ thiếu dấu gạch là lỗi của bàn phím, không phải ý định.
  robotsDisallow: cleanPaths(f.robotsDisallow),
  robotsExtra: f.robotsExtra.trim(),
  sitemapExclude: cleanPaths(f.sitemapExclude),
})

/** Dọn danh sách đường dẫn: bỏ dòng trống, bỏ trùng, bảo đảm có `/` đầu. */
const cleanPaths = (list: string[]): string[] => {
  const out = list.map((v) => v.trim()).filter(Boolean).map((v) => (v.startsWith('/') ? v : `/${v}`))
  return [...new Set(out)]
}

/**
 * Cấu hình SEO dùng cho **mọi** trang trên web.
 *
 * Đây là những thứ người làm SEO cần đổi mà không nên phải sửa code: tên thương hiệu,
 * mẫu tiêu đề, mô tả dự phòng, mã Search Console, thông tin doanh nghiệp cho JSON-LD.
 */
export default function SettingsPage() {
  const toast = useToast()
  const [form, setForm] = useState<Form | null>(null)
  const [initial, setInitial] = useState<Form | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [homeAdvanced, setHomeAdvanced] = useState(false)

  useEffect(() => {
    api.get().then((s) => { const f = fromDoc(s); setForm(f); setInitial(f) })
      .catch((e) => toast.error(explainError(e).message))
  }, [toast])

  const dirty = useMemo(() => !!form && !!initial && JSON.stringify(form) !== JSON.stringify(initial), [form, initial])
  useDirtyGuard(dirty)

  if (!form) return <Spinner />
  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))

  const save = async () => {
    setBusy(true); setErrors({})
    try {
      const saved = await api.update(toBody(form))
      const f = fromDoc(saved)
      setForm(f); setInitial(f)
      toast.success('Đã lưu cài đặt')
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      toast.error(message)
    } finally { setBusy(false) }
  }

  /** Host hiện trong bản xem trước SERP. `siteUrl` trống thì đoán theo domain thật. */
  const siteHost = (form.siteUrl || 'https://tiemnhanho.vn').replace(/^https?:\/\//, '').replace(/\/$/, '')

  const previewTitle = renderTitleTemplate(form.titleTemplate, {
    title: 'Móc khoá cáo in 3D', sitename: form.siteName, separator: form.titleSeparator,
  })

  return (
    <>
      <PageHeader title="Cài đặt SEO" subtitle="Áp dụng cho toàn bộ web — sản phẩm và bài viết kế thừa từ đây"
        actions={<Button onClick={save} loading={busy}>{dirty ? 'Lưu' : 'Đã lưu'}</Button>} />

      {!form.allowIndexing && (
        <p className="mb-4 rounded-card-sm bg-danger-tint px-4 py-3 text-sm text-danger">
          Toàn site đang <strong>chặn Google</strong>. Không trang nào lên được kết quả tìm kiếm cho tới khi bật lại công tắc bên dưới.
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Thương hiệu & tiêu đề">
          <div className="space-y-4">
            <Field label="Tên shop" required hint="Dùng cho %sitename%, schema Organization và thẻ chia sẻ" error={errors.siteName}>
              <Input value={form.siteName} onChange={(e) => patch({ siteName: e.target.value })} />
            </Field>
            <Field label="Mẫu tiêu đề" required error={errors.titleTemplate}
              hint="Biến dùng được: %title% (tên trang), %sitename%, %category%. Bắt buộc có %title%.">
              <Input value={form.titleTemplate} onChange={(e) => patch({ titleTemplate: e.target.value })} className="font-mono text-xs" />
            </Field>
            <Field label="Dấu phân cách" hint="Ký tự giữa các phần, vd | hoặc –" error={errors.titleSeparator}>
              <Input value={form.titleSeparator} maxLength={3} onChange={(e) => patch({ titleSeparator: e.target.value })} className="w-20 font-mono" />
            </Field>
            <div className="rounded-card-sm bg-sand p-3">
              <p className="text-xs text-ink-muted">Tiêu đề sẽ hiện trên Google:</p>
              <p className="mt-0.5 text-sm text-[#1a0dab]">{previewTitle}</p>
            </div>
            <Field label="Thương hiệu mặc định cho sản phẩm" hint="Điền khi sản phẩm không tự đặt brand" error={errors.defaultBrand}>
              <Input value={form.defaultBrand} onChange={(e) => patch({ defaultBrand: e.target.value })} placeholder={form.siteName} />
            </Field>
          </div>
        </Card>

        <Card title="Mô tả & ảnh dự phòng">
          <div className="space-y-4">
            <Field label="Meta description mặc định" error={errors.defaultMetaDescription}
              hint="Dùng khi một trang không tự đặt mô tả. Nên 120–160 ký tự, có nói shop làm gì.">
              <Textarea value={form.defaultMetaDescription} maxLength={500} onChange={(e) => patch({ defaultMetaDescription: e.target.value })} />
            </Field>
            <Field as="div" label="Ảnh chia sẻ mặc định" hint="1200×630. Dùng khi trang không có ảnh OG riêng." error={errors.defaultOgImage}>
              <ImageField value={form.defaultOgImage} onChange={(defaultOgImage) => patch({ defaultOgImage })} />
            </Field>
          </div>
        </Card>

        {/*
          Trang chủ có khối SEO riêng, **không** mount cả `SeoPanel`.

          `SeoPanel` chấm điểm cả nội dung (số từ, heading, link nội bộ). Nội dung trang chủ
          nằm trong component React chứ không trong DB, nên điểm sẽ mãi ở mức kém — và một
          chỉ số luôn đỏ là một chỉ số người ta học cách bỏ qua. Ở đây chỉ giữ phần đo được
          thật: chiều rộng pixel và bản xem trước.
        */}
        <Card title="Trang chủ (SEO riêng)">
          <div className="space-y-4">
            <p className="text-xs text-ink-muted">
              Chỉ áp dụng cho <code className="font-mono">/</code>. Để trống thì trang chủ dùng tên shop và mô tả mặc định ở trên.
            </p>

            <Field label="Tiêu đề trên Google" hint="Trống → tên shop kèm khẩu hiệu của web. Tiêu đề đã chứa tên shop thì mẫu tiêu đề không áp thêm lần nữa." error={errors['home.title']}>
              <Input value={form.home.title} maxLength={200} onChange={(e) => patch({ home: { ...form.home, title: e.target.value } })} placeholder={form.siteName} />
              {!!form.home.title && (
                <PixelMeter px={textPixels(form.home.title, 20)} min={SEO_LIMITS.titlePixels.min} max={SEO_LIMITS.titlePixels.max} />
              )}
            </Field>

            <Field label="Meta description" hint="Trống → mô tả mặc định ở trên." error={errors['home.description']}>
              <Textarea value={form.home.description} maxLength={500} rows={3} onChange={(e) => patch({ home: { ...form.home, description: e.target.value } })} placeholder={form.defaultMetaDescription} />
              {!!form.home.description && (
                <PixelMeter px={textPixels(form.home.description, 14)} min={SEO_LIMITS.descPixels.min} max={SEO_LIMITS.descPixels.max} />
              )}
            </Field>

            {/*
              Chỉ xem trước khi đã đặt riêng. Khi để trống, tiêu đề thật do `apps/web` ghép
              từ tên shop + khẩu hiệu nằm trong `lib/site.ts` — admin **không** biết chuỗi đó.
              Chép nó sang đây để vẽ cho đẹp là tạo bản sao thứ hai của một hằng số, đúng kiểu
              lỗi `.prose-nhanho`: hai bên lệch nhau mà không có gì báo. Thà không vẽ.
            */}
            {form.home.title || form.home.description ? (
              <SerpPreview title={form.home.title || form.siteName} description={form.home.description || form.defaultMetaDescription} url={siteHost} device="desktop" />
            ) : (
              <p className="rounded-button bg-sand px-3 py-2 text-xs text-ink-muted">
                Chưa đặt riêng — trang chủ đang dùng tiêu đề và mô tả mặc định.
              </p>
            )}

            <div className="rounded-card-sm border border-border">
              <button type="button" onClick={() => setHomeAdvanced((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-ink hover:bg-sand">
                <span>Nâng cao: robots, canonical, chia sẻ, JSON-LD</span>
                <span className="text-ink-muted">{homeAdvanced ? '▲' : '▼'}</span>
              </button>
              {homeAdvanced && (
                <div className="space-y-4 border-t border-border p-4">
                  <Toggle checked={form.home.noindex} onChange={(noindex) => patch({ home: { ...form.home, noindex } })} label="noindex — chặn riêng trang chủ khỏi Google" />
                  {form.home.noindex && <p className="text-xs text-danger">Trang chủ sẽ không lên Google. Đây gần như luôn là điều bạn không muốn.</p>}
                  <Toggle checked={form.home.nofollow} onChange={(nofollow) => patch({ home: { ...form.home, nofollow } })} label="nofollow — không truyền uy tín qua link trên trang chủ" />
                  <Field label="Canonical" hint="Để trống là an toàn nhất." error={errors['home.canonical']}>
                    <Input value={form.home.canonical} className="font-mono text-xs" placeholder="https://tiemnhanho.vn" onChange={(e) => patch({ home: { ...form.home, canonical: e.target.value } })} />
                  </Field>
                  <Field label="Tiêu đề khi chia sẻ (og:title)" error={errors['home.ogTitle']}>
                    <Input value={form.home.ogTitle} maxLength={200} placeholder={form.home.title || form.siteName} onChange={(e) => patch({ home: { ...form.home, ogTitle: e.target.value } })} />
                  </Field>
                  <Field label="Mô tả khi chia sẻ (og:description)" error={errors['home.ogDescription']}>
                    <Textarea value={form.home.ogDescription} maxLength={500} placeholder={form.home.description || form.defaultMetaDescription} onChange={(e) => patch({ home: { ...form.home, ogDescription: e.target.value } })} />
                  </Field>
                  <Field as="div" label="Ảnh chia sẻ riêng của trang chủ" hint="Trống → dùng ảnh chia sẻ mặc định ở trên." error={errors['home.image']}>
                    <ImageField value={form.home.image} onChange={(image) => patch({ home: { ...form.home, image } })} />
                  </Field>
                  <SocialPreview image={form.home.image ?? form.defaultOgImage}
                    title={form.home.ogTitle || form.home.title || form.siteName}
                    description={form.home.ogDescription || form.home.description || form.defaultMetaDescription}
                    host={siteHost} />
                  <JsonLdField value={form.home.jsonLd} onChange={(jsonLd) => patch({ home: { ...form.home, jsonLd } })} error={errors['home.jsonLd']} />
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card title="Kỹ thuật">
          <div className="space-y-4">
            <div>
              <Toggle
                checked={form.allowIndexing}
                onChange={(v) => { if (!v) setConfirmBlock(true); else patch({ allowIndexing: true }) }}
                label="Cho phép Google lập chỉ mục toàn site"
              />
              <p className="mt-1 text-xs text-ink-muted">Tắt khi web đang dựng. Nhớ bật lại trước khi chạy thật — tắt là mất toàn bộ traffic tìm kiếm.</p>
            </div>
            <Field label="Địa chỉ web" hint="URL đầy đủ, vd https://tiemnhanho.vn — dùng cho canonical và sitemap" error={errors.siteUrl}>
              <Input value={form.siteUrl} onChange={(e) => patch({ siteUrl: e.target.value })} placeholder="https://tiemnhanho.vn" className="font-mono text-xs" />
            </Field>
            <Field label="Mã xác minh Google Search Console" hint="Chỉ dán phần nội dung của thẻ google-site-verification" error={errors.googleSiteVerification}>
              <Input value={form.googleSiteVerification} onChange={(e) => patch({ googleSiteVerification: e.target.value })} className="font-mono text-xs" />
            </Field>
            <Field label="Google Analytics 4" hint="Dạng G-XXXXXXXXXX. Để trống thì không nhúng." error={errors.gaMeasurementId}>
              <Input value={form.gaMeasurementId} onChange={(e) => patch({ gaMeasurementId: e.target.value.toUpperCase() })} placeholder="G-XXXXXXXXXX" className="font-mono text-xs" />
            </Field>
          </div>
        </Card>

        {/*
          Danh sách đường dẫn nhập theo **mỗi dòng một đường dẫn** chứ không phải hàng ô như
          Menu chính: đây đúng là hình dạng của robots.txt, và dán từ file có sẵn là việc
          người ta sẽ làm. Mảng giữ nguyên cả dòng trống trong lúc gõ; `toBody` mới dọn.
        */}
        <Card title="robots.txt & sitemap">
          <div className="space-y-4">
            <p className="text-xs text-ink-muted">
              Mỗi dòng một đường dẫn, bắt đầu bằng <code>/</code>. Để <strong>trống hoàn toàn</strong>
              thì web dùng danh sách chặn mặc định (<code>/api/</code>, <code>/tai-khoan</code>,{' '}
              <code>/gio-hang</code>, <code>/tim-kiem</code>) — xoá hết ở đây không mở toang site.
            </p>

            {!form.allowIndexing && (
              <p className="rounded-button bg-danger-tint px-3 py-2 text-xs text-danger">
                Toàn site đang chặn Google, nên <code>robots.txt</code> hiện chỉ trả{' '}
                <code>Disallow: /</code>. Các mục dưới đây chưa có tác dụng cho tới khi bật lại công tắc.
              </p>
            )}

            <Field label="Chặn crawl (Disallow)" error={errors.robotsDisallow}>
              <Textarea
                value={form.robotsDisallow.join('\n')}
                rows={4}
                spellCheck={false}
                className="font-mono text-xs"
                placeholder={'/gio-hang\n/tim-kiem'}
                onChange={(e) => patch({ robotsDisallow: e.target.value.split('\n') })}
              />
            </Field>

            <Field label="Dòng thêm vào cuối robots.txt" hint="Cho các chỉ thị không có ô riêng, vd Crawl-delay hay sitemap ảnh. Chỉ in ra khi site đang cho index." error={errors.robotsExtra}>
              <Textarea
                value={form.robotsExtra}
                rows={3}
                spellCheck={false}
                maxLength={2000}
                className="font-mono text-xs"
                placeholder={'Sitemap: https://tiemnhanho.vn/sitemap-images.xml'}
                onChange={(e) => patch({ robotsExtra: e.target.value })}
              />
            </Field>

            <Field label="Loại khỏi sitemap.xml" hint="Kết thúc bằng / để loại cả nhánh, vd /chinh-sach/" error={errors.sitemapExclude}>
              <Textarea
                value={form.sitemapExclude.join('\n')}
                rows={3}
                spellCheck={false}
                className="font-mono text-xs"
                placeholder={'/san-pham/a'}
                onChange={(e) => patch({ sitemapExclude: e.target.value.split('\n') })}
              />
            </Field>

            {/* Không vẽ bản xem trước ở đây: sẽ thành bản sao thứ hai của luật sinh file. */}
            <p className="text-xs text-ink-muted">
              Kiểm chứng bằng file thật:{' '}
              <a className="underline" href={`https://${siteHost}/robots.txt`} target="_blank" rel="noreferrer">robots.txt</a>
              {' · '}
              <a className="underline" href={`https://${siteHost}/sitemap.xml`} target="_blank" rel="noreferrer">sitemap.xml</a>
            </p>
          </div>
        </Card>

        <Card title="Menu chính">
          <div className="space-y-4">
            <p className="text-xs text-ink-muted">
              Thanh menu trên đầu website. Để <strong>trống hoàn toàn</strong> thì web dùng menu
              mặc định cài sẵn trong mã nguồn — nên xoá hết ở đây không làm mất menu.
              Đường dẫn nội bộ bắt đầu bằng <code>/</code>; dán link đầy đủ nếu trỏ ra ngoài.
            </p>
            {form.mainNav.length === 0 && (
              <p className="rounded-button bg-sand px-3 py-2 text-xs text-ink-muted">
                Đang dùng menu mặc định. Thêm mục đầu tiên để tự quản lý.
              </p>
            )}
            <div className="space-y-2">
              {form.mainNav.map((item, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
                  <Input
                    value={item.label}
                    placeholder="Nhãn hiện ra"
                    onChange={(e) => patch({ mainNav: form.mainNav.map((n, j) => (j === i ? { ...n, label: e.target.value } : n)) })}
                  />
                  <Input
                    value={item.href}
                    placeholder="/ve-nha-nho"
                    className="font-mono text-xs"
                    onChange={(e) => patch({ mainNav: form.mainNav.map((n, j) => (j === i ? { ...n, href: e.target.value } : n)) })}
                  />
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Lên"
                      disabled={i === 0}
                      onClick={() => {
                        const next = [...form.mainNav]
                        const tmp = next[i - 1]!
                        next[i - 1] = next[i]!
                        next[i] = tmp
                        patch({ mainNav: next })
                      }}
                    >↑</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Xuống"
                      disabled={i === form.mainNav.length - 1}
                      onClick={() => {
                        const next = [...form.mainNav]
                        const tmp = next[i + 1]!
                        next[i + 1] = next[i]!
                        next[i] = tmp
                        patch({ mainNav: next })
                      }}
                    >↓</Button>
                    <Button size="sm" variant="ghost" className="text-danger" aria-label="Xoá"
                      onClick={() => patch({ mainNav: form.mainNav.filter((_, j) => j !== i) })}>×</Button>
                  </div>
                </div>
              ))}
            </div>
            {errors.mainNav && <p className="text-xs text-danger">{errors.mainNav}</p>}
            <Button size="sm" variant="secondary"
              onClick={() => patch({ mainNav: [...form.mainNav, { label: '', href: '', order: form.mainNav.length }] })}>
              + Thêm mục
            </Button>
          </div>
        </Card>

        <Card title="Thông tin doanh nghiệp (schema Organization)">
          <div className="space-y-4">
            <p className="text-xs text-ink-muted">Google dùng khối này để hiểu shop là ai. Điền đủ giúp tăng độ tin cậy (E-E-A-T) và có thể hiện knowledge panel.</p>
            <Field label="Tên pháp lý" error={errors['organization.legalName']}>
              <Input value={form.org.legalName} onChange={(e) => patch({ org: { ...form.org, legalName: e.target.value } })} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Điện thoại" error={errors['organization.phone']}>
                <Input value={form.org.phone} onChange={(e) => patch({ org: { ...form.org, phone: e.target.value } })} />
              </Field>
              <Field label="Email" error={errors['organization.email']}>
                <Input type="email" value={form.org.email} onChange={(e) => patch({ org: { ...form.org, email: e.target.value } })} />
              </Field>
            </div>
            <Field label="Địa chỉ" error={errors['organization.address']}>
              <Input value={form.org.address} onChange={(e) => patch({ org: { ...form.org, address: e.target.value } })} />
            </Field>
            <Field label="Link mạng xã hội" hint="Facebook, Instagram, TikTok, Shopee… Enter để thêm." error={errors['organization.sameAs']}>
              <TagInput value={form.org.sameAs} onChange={(sameAs) => patch({ org: { ...form.org, sameAs } })} placeholder="https://facebook.com/…" />
            </Field>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmBlock}
        title="Chặn Google toàn bộ web?"
        confirmLabel="Chặn Google"
        onCancel={() => setConfirmBlock(false)}
        onConfirm={() => { setConfirmBlock(false); patch({ allowIndexing: false }) }}
        message={<>Mọi trang sẽ mang thẻ <code>noindex</code>. Google sẽ dần loại toàn bộ web khỏi kết quả tìm kiếm, và phục hồi mất nhiều tuần. Chỉ làm khi web đang dựng.</>}
      />
    </>
  )
}
